import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { PasswordChangeForm } from '../components'
import { config } from '../config/environment'

import { Helmet } from 'react-helmet-async'

/**
 * Change password for an authenticated user by calling the Cognito API directly.
 * Uses the stored access token and the Cognito ChangePassword action.
 */
async function changeAuthenticatedPassword(oldPassword: string, newPassword: string): Promise<void> {
  // Get the access token from localStorage
  const stored = localStorage.getItem('fwo_auth_session')
  if (!stored) {
    throw new Error('No active session found. Please log in again.')
  }

  const session = JSON.parse(stored)
  const accessToken = session.accessToken
  if (!accessToken) {
    throw new Error('No access token found. Please log in again.')
  }

  // Call Cognito ChangePassword API directly
  const region = config.cognito.region || 'eu-west-2'
  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
    },
    body: JSON.stringify({
      AccessToken: accessToken,
      PreviousPassword: oldPassword,
      ProposedPassword: newPassword,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorType = errorData.__type || ''
    
    if (errorType.includes('NotAuthorizedException')) {
      throw new Error('Current password is incorrect.')
    } else if (errorType.includes('InvalidPasswordException')) {
      throw new Error('New password does not meet requirements.')
    } else if (errorType.includes('LimitExceededException')) {
      throw new Error('Too many attempts. Please try again later.')
    } else if (errorType.includes('InvalidParameterException')) {
      throw new Error('Invalid password format.')
    } else {
      throw new Error(errorData.message || 'Failed to change password. Please try again.')
    }
  }
}

export default function ChangePasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { user, isLoading, isAuthenticated } = useAuth()
  const [sessionToken, setSessionToken] = useState<string>('')
  const [userEmail, setUserEmail] = useState<string>('')
  const [mode, setMode] = useState<'first-login' | 'change-password'>('first-login')
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (!isClient) return
    // Get session token, email from location state (preferred) or query params (fallback)
    const locationState = location.state as { email?: string; session?: string; user?: any } | null
    const session = locationState?.session || searchParams.get('session')
    const email = locationState?.email || searchParams.get('email')
    
    if (session) {
      setSessionToken(session)
      setMode('first-login') // Coming from NEW_PASSWORD_REQUIRED challenge
    } else if (isAuthenticated && mode !== 'first-login') {
      // Only switch to change-password mode if we didn't arrive here from
      // a first-login flow (which would have already navigated away on success)
      setMode('change-password') // User is changing password while logged in
    } else if (!isAuthenticated && !session) {
      // No session token and not authenticated - redirect to login
      navigate('/login')
      return
    }
    
    if (email) {
      setUserEmail(email)
    }
  }, [searchParams, location.state, isAuthenticated, navigate, isClient])

  const { changePassword } = useAuth()

  const handlePasswordChange = async (newPassword: string, oldPassword?: string): Promise<boolean> => {
    try {
      if (mode === 'first-login' && sessionToken && userEmail) {
        // Complete NEW_PASSWORD_REQUIRED challenge using the auth hook
        const response = await changePassword(userEmail, sessionToken, newPassword)
        
        if (response.tokens && response.user) {
          // Successful password change and authentication - navigate to dashboard
          setSessionToken('')
          navigate('/dashboard')
          return true
        }
        
        return false
      } else if (mode === 'change-password' && isAuthenticated && oldPassword) {
        // User is changing password while logged in - call Cognito directly
        await changeAuthenticatedPassword(oldPassword, newPassword)
        return true
      }
      
      return false
    } catch (error) {
      console.error('Password change failed:', error)
      return false
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/fwo-logo.png" 
            alt="FlyWideOpen" 
            className="h-16 mx-auto mb-4 opacity-50 animate-pulse" 
          />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // If no session token and not authenticated, redirect to login
  if (!sessionToken && !isAuthenticated) {
    return null // Will redirect via useEffect
  }

  return (
    <>
      <Helmet>
        <title>
          {mode === 'first-login' ? 'Set Your Password' : 'Change Password'} - FWO Pilot Portal
        </title>
        <meta name="description" content="Set or change your password for the FlyWideOpen Pilot Portal" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-3">
                <img 
                  src="/fwo-logo.png" 
                  alt="FlyWideOpen" 
                  className="h-10" 
                />
                <h1 className="text-xl font-bold text-gray-900">Pilot Portal</h1>
              </Link>
              
              {/* Show user info if authenticated */}
              {isAuthenticated && user && (
                <div className="text-sm text-gray-600">
                  {user?.firstName} {user?.lastName}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {mode === 'first-login' ? 'Secure Your Account' : 'Change Your Password'}
            </h2>
            <p className="text-lg text-gray-600">
              {mode === 'first-login' 
                ? 'Please set a secure password for your account'
                : 'Update your password to keep your account secure'
              }
            </p>
            {userEmail && (
              <p className="text-sm text-gray-500 mt-2">
                Account: {userEmail}
              </p>
            )}
          </div>

          <PasswordChangeForm
            onPasswordChange={handlePasswordChange}
            isLoading={false}
            sessionToken={sessionToken}
            mode={mode}
          />

          {/* Navigation Links */}
          <div className="mt-6 text-center">
            {mode === 'first-login' ? (
              <Link 
                to="/login" 
                className="text-sm text-fwo-blue hover:text-blue-700 underline"
              >
                Back to Login
              </Link>
            ) : (
              <Link 
                to="/dashboard" 
                className="text-sm text-fwo-blue hover:text-blue-700 underline"
              >
                Back to Dashboard
              </Link>
            )}
          </div>

          {/* Help Section */}
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Password Requirements</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>Your password must contain:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>At least 8 characters</li>
                  <li>One uppercase letter (A-Z)</li>
                  <li>One lowercase letter (a-z)</li>
                  <li>One number (0-9)</li>
                  <li>One special character (!@#$%^&*)</li>
                </ul>
                <p className="mt-4 text-xs text-gray-500">
                  This password will be used for all future logins to the portal. Keep it secure and don't share it with others.
                </p>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center text-gray-600">
              <p>&copy; {new Date().getFullYear()} FlyWideOpen. All rights reserved.</p>
              <p className="mt-2 text-sm">
                Paragliding competition management platform
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}